import { z } from 'zod';
import { Candidate, GeoIdentity, Id, Rect, parse } from './contracts.mjs';

export const INSPECT_OPERATIONS = Object.freeze({
  hitTest: 'inspect.hitTest',
  rects: 'inspect.rects',
  scroll: 'inspect.scroll',
});
export function parseCandidate(raw) {
  try {
    return parse(Candidate, raw);
  } catch {
    return null;
  }
}
export const HitTestInput = z.strictObject({
  identity: GeoIdentity,
  point: z.strictObject({ x: z.number().finite(), y: z.number().finite() }),
  intent: z.enum(['hover', 'click']),
  seq: z.int().positive(),
});
export const HitTestResult = z.strictObject({
  identity: GeoIdentity,
  seq: z.int().positive(),
  candidate: Candidate.nullable(),
});
export const RectsInput = z.strictObject({ identity: GeoIdentity, targetIds: z.array(Id).max(16) });
export const RectsResult = z.strictObject({
  identity: GeoIdentity,
  targets: z.array(z.strictObject({ targetId: Id, rects: z.array(Rect).max(8) })).max(16),
});
export const ScrollInput = z.strictObject({
  identity: GeoIdentity,
  dx: z.number().finite().min(-2000).max(2000),
  dy: z.number().finite().min(-2000).max(2000),
});
export function inspectPayload(schema, input) {
  const value = parse(schema, input);
  if (new TextEncoder().encode(JSON.stringify(value)).length > 16384)
    throw new TypeError('inspect_payload_limit');
  return value;
}
export function sameGeometry(left, right) {
  return ['sessionId', 'revision', 'docGeneration', 'requestId', 'space', 'epoch'].every(
    (field) => left[field] === right[field],
  );
}

// These are child-side observation handlers registered in RUN's transport.
// The trusted host registers no incoming inspect action/send/apply handler.
export function createInspectObservationHandlers({ hitTest, rects, scroll }) {
  return {
    [INSPECT_OPERATIONS.hitTest]: {
      request: async (raw, { signal }) => {
        const input = inspectPayload(HitTestInput, raw);
        const candidate = await hitTest(input.point, input.identity, { signal });
        return inspectPayload(HitTestResult, {
          identity: input.identity,
          seq: input.seq,
          candidate,
        });
      },
    },
    [INSPECT_OPERATIONS.rects]: {
      request: async (raw, { signal }) => {
        const input = inspectPayload(RectsInput, raw);
        const targets = await rects(input.targetIds, input.identity, { signal });
        return inspectPayload(RectsResult, { identity: input.identity, targets });
      },
    },
    [INSPECT_OPERATIONS.scroll]: {
      request: async (raw) => {
        const input = inspectPayload(ScrollInput, raw);
        await scroll(input.dx, input.dy);
        return null;
      },
    },
  };
}
